import { useEffect, useState, type ReactNode } from "react";
import { Check, Smartphone, Apple } from "lucide-react";
import { Sheet } from "@/components/shell/Sheet";
import {
  AndroidInstallIllustration,
  AndroidMenuIllustration,
  IosAddHomeIllustration,
  IosShareIllustration,
} from "./InstallIllustration";
import {
  getPlatform,
  isIosSafari,
  isStandalone,
  type Platform,
} from "@/lib/utils/pwa";
import { cn } from "@/lib/utils/cn";

type Tab = "ios" | "android";

type Props = {
  open: boolean;
  onClose: () => void;
};

/**
 * Bottom sheet z instrukcją instalacji PWA. Auto-wybiera zakładkę na
 * podstawie platformy usera, ale daje przełącznik (komputer rodzica
 * pokazujący instrukcję dla telefonu dziecka, itp.).
 *
 * Gdy apka już działa jako standalone — pokazuje stan "✓ zainstalowane"
 * zamiast kroków, żeby user nie kombinował niepotrzebnie.
 */
export function InstallAppSheet({ open, onClose }: Props) {
  const [platform, setPlatform] = useState<Platform>("desktop");
  const [tab, setTab] = useState<Tab>("ios");
  const [standalone, setStandalone] = useState(false);
  const [iosSafari, setIosSafari] = useState(true);

  useEffect(() => {
    if (!open) return;
    const p = getPlatform();
    setPlatform(p);
    setTab(p === "android" ? "android" : "ios");
    setStandalone(isStandalone());
    setIosSafari(isIosSafari());
  }, [open]);

  return (
    <Sheet open={open} onClose={onClose} title="📱 Zainstaluj jako apkę">
      {standalone ? <AlreadyInstalled /> : null}

      {!standalone && (
        <>
          <p className="-mt-1 mb-3 text-[12px] text-muted leading-relaxed">
            Sianko działa lepiej zainstalowane: ikona na ekranie głównym,
            pełnoekranowy widok bez paska adresu i poprawne powiadomienia.
            Bez konieczności pobierania ze sklepu.
          </p>

          {/* Przełącznik zakładek */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <TabButton
              active={tab === "ios"}
              onClick={() => setTab("ios")}
              icon={<Apple className="h-3.5 w-3.5" />}
              label="iPhone / iPad"
            />
            <TabButton
              active={tab === "android"}
              onClick={() => setTab("android")}
              icon={<Smartphone className="h-3.5 w-3.5" />}
              label="Android"
            />
          </div>

          {tab === "ios" ? (
            <IosSteps showSafariWarning={platform === "ios" && !iosSafari} />
          ) : (
            <AndroidSteps />
          )}

          {platform === "desktop" && (
            <p className="mt-4 text-[11px] text-muted leading-relaxed border-t border-line pt-3">
              💡 Otwierasz to na komputerze. Najwygodniej zainstalować
              Sianko bezpośrednio na telefonie — otwórz tę instrukcję
              z telefonu albo wpisz w przeglądarce telefonu adres tej
              aplikacji.
            </p>
          )}
        </>
      )}
    </Sheet>
  );
}

function AlreadyInstalled() {
  return (
    <div className="flex flex-col items-center text-center py-6">
      <div className="h-14 w-14 rounded-full bg-emerald-500/15 ring-2 ring-emerald-500/30 flex items-center justify-center mb-3">
        <Check className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
      </div>
      <p className="text-base font-semibold">Już zainstalowane</p>
      <p className="mt-1 text-sm text-muted max-w-[280px]">
        Sianko działa jako aplikacja na tym urządzeniu. Wszystko gra.
      </p>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-10 rounded-[12px] text-sm font-medium border transition-colors flex items-center justify-center gap-2",
        active
          ? "bg-ink text-card border-ink"
          : "bg-card text-ink border-line hover:bg-soft",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function StepCard({
  number,
  title,
  hint,
  children,
}: {
  number: number;
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-[14px] border border-line bg-card overflow-hidden">
      <div className="flex items-center gap-2.5 px-4 pt-3 pb-2">
        <span className="h-6 w-6 rounded-full bg-accent text-white text-[12px] font-bold flex items-center justify-center shrink-0">
          {number}
        </span>
        <h4 className="text-sm font-semibold text-ink leading-tight">
          {title}
        </h4>
      </div>
      {hint && (
        <p className="px-4 pb-2 text-[11px] text-muted leading-relaxed">
          {hint}
        </p>
      )}
      <div className="px-4 pb-3 pt-1">{children}</div>
    </div>
  );
}

function IosSteps({ showSafariWarning }: { showSafariWarning: boolean }) {
  return (
    <div className="space-y-3">
      {showSafariWarning && (
        <div className="rounded-[12px] border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[12px] text-ink leading-snug">
          ⚠️ Otwórz Sianko w <strong>Safari</strong> — w Chrome ani Firefox
          na iPhone instalacja PWA nie działa (ograniczenie Apple).
        </div>
      )}

      <StepCard
        number={1}
        title="Tapnij ikonę Udostępnij"
        hint="Na dolnym pasku Safari, w środku — kwadrat ze strzałką w górę."
      >
        <IosShareIllustration />
      </StepCard>

      <StepCard
        number={2}
        title="Wybierz Dodaj do ekranu początkowego"
        hint="Przewiń w dół jeśli nie widzisz tej opcji od razu."
      >
        <IosAddHomeIllustration />
      </StepCard>

      <StepCard
        number={3}
        title="Tapnij Dodaj"
        hint="W prawym górnym rogu. Możesz wcześniej zmienić nazwę ikony."
      >
        <p className="text-[12px] text-muted leading-relaxed">
          Sianko pojawi się na ekranie początkowym jak każda inna aplikacja.
          Otwórz ją z ikony — odpalisz pełnoekranowy tryb bez paska adresu
          Safari.
        </p>
      </StepCard>
    </div>
  );
}

function AndroidSteps() {
  return (
    <div className="space-y-3">
      <StepCard
        number={1}
        title="Otwórz menu Chrome"
        hint="Trzy kropki w prawym górnym rogu (⋮)."
      >
        <AndroidMenuIllustration />
      </StepCard>

      <StepCard
        number={2}
        title="Wybierz Zainstaluj aplikację"
        hint="Czasem nazywa się to Dodaj do ekranu głównego — kliknij tę pozycję."
      >
        <AndroidInstallIllustration />
      </StepCard>

      <StepCard
        number={3}
        title="Potwierdź Zainstaluj"
        hint="Chrome zapyta o potwierdzenie. Tapnij Zainstaluj — Sianko trafi na ekran domowy."
      >
        <p className="text-[12px] text-muted leading-relaxed">
          Po zainstalowaniu otwieraj Sianko z ikony, nie z przeglądarki —
          pełnoekranowy widok i lepsze powiadomienia.
        </p>
      </StepCard>
    </div>
  );
}
